import { useEffect, useRef } from 'react';
import { ImageChainMetadata } from './useHeroImage';

/**
 * Custom hook to handle chain metadata callback notifications.
 * Prevents unnecessary callbacks by tracking previous metadata state
 * and only invoking the callback when metadata actually changes.
 * 
 * @param onChainMetadata - Callback function to notify parent of chain metadata changes
 * @param trackChain - Whether to track and notify about chain metadata
 * @param heroChainMetadata - Hero image chain metadata
 * @param avatarChainMetadata - Avatar image chain metadata
 */
export const useChainMetadataCallback = (
  onChainMetadata?: (heroChain: ImageChainMetadata | null, avatarChain: ImageChainMetadata | null) => void,
  trackChain?: boolean,
  heroChainMetadata?: ImageChainMetadata | null,
  avatarChainMetadata?: ImageChainMetadata | null
) => {
  // Track previous metadata to prevent unnecessary callbacks
  const prevMetadataRef = useRef<{ hero: ImageChainMetadata | null; avatar: ImageChainMetadata | null } | null>(null);
  const callbackRef = useRef(onChainMetadata);

  // Keep callback ref up to date
  useEffect(() => {
    callbackRef.current = onChainMetadata;
  }, [onChainMetadata]);

  // Notify parent of chain metadata if callback provided
  useEffect(() => {
    if (!callbackRef.current || !trackChain) return;

    // Only call if metadata exists and has changed
    const hasMetadata = heroChainMetadata || avatarChainMetadata;
    if (!hasMetadata) return;

    // Check if metadata has actually changed
    // Note: Reference equality is sufficient here because React state maintains
    // stable references when content doesn't change. The hooks only update
    // chainMetadata when the actual validation results change.
    const prevMetadata = prevMetadataRef.current;
    const metadataChanged =
      !prevMetadata ||
      prevMetadata.hero !== heroChainMetadata ||
      prevMetadata.avatar !== avatarChainMetadata;

    if (metadataChanged) {
      prevMetadataRef.current = { hero: heroChainMetadata || null, avatar: avatarChainMetadata || null };
      callbackRef.current(heroChainMetadata || null, avatarChainMetadata || null);
    }
  }, [trackChain, heroChainMetadata, avatarChainMetadata]);
};

